import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { Globe, Play, Save, FolderOpen, Trash2, Plus, X, Loader, Lock } from 'lucide-react'
import api from '../api'

const METHODS   = ['GET','POST','PUT','PATCH','DELETE','HEAD','OPTIONS']
const BODY_TYPES = ['none','json','form-urlencoded','raw','xml']
const AUTH_TYPES = ['none','basic','bearer','api-key']

const METHOD_COLORS = {
  GET:'#34d399', POST:'#fbbf24', PUT:'#38bdf8',
  PATCH:'#a78bfa', DELETE:'#f87171', HEAD:'#94a3b8', OPTIONS:'#64748b',
}

/* ─── KV Editor ─── */
function KVEditor({ label, items, onChange }) {
  const add    = () => onChange([...items, { key:'', value:'', enabled:true }])
  const remove = i  => onChange(items.filter((_,idx) => idx !== i))
  const update = (i, field, val) => {
    const next = [...items]; next[i] = { ...next[i], [field]: val }; onChange(next)
  }
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <span style={{ fontSize:13, color:'var(--text3)' }}>{label}</span>
        <button onClick={add} style={{ fontSize:13, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
          <Plus size={12} /> Thêm
        </button>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {items.length === 0 && (
          <div style={{ fontSize:13, color:'var(--text3)', textAlign:'center', padding:'16px', border:'1px dashed var(--border)', borderRadius:8 }}>
            Chưa có {label}. Nhấn "Thêm" để bắt đầu.
          </div>
        )}
        {items.map((item, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
            <input type="checkbox" checked={item.enabled}
              onChange={e => update(i,'enabled',e.target.checked)}
              style={{ flexShrink:0, accentColor:'var(--accent)', width:15, height:15 }} />
            <input className="inp" style={{ flex:1, padding:'7px 10px', fontSize:13 }}
              placeholder="Key" value={item.key}
              onChange={e => update(i,'key',e.target.value)} />
            <input className="inp" style={{ flex:1, padding:'7px 10px', fontSize:13 }}
              placeholder="Value" value={item.value}
              onChange={e => update(i,'value',e.target.value)} />
            <button onClick={() => remove(i)}
              style={{ color:'var(--danger)', background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Main Component ─── */
export default function HttpTester({ user }) {
  const [method,  setMethod]  = useState('GET')
  const [url,     setUrl]     = useState('https://httpbin.org/get')
  const [headers, setHeaders] = useState([{ key:'Accept', value:'application/json', enabled:true }])
  const [params,  setParams]  = useState([])
  const [bodyType,setBodyType]= useState('none')
  const [body,    setBody]    = useState('{\n  \n}')
  const [authType,setAuthType]= useState('none')
  const [auth,    setAuth]    = useState({ username:'', password:'', token:'', key:'', value:'' })
  const [result,  setResult]  = useState(null)
  const [loading, setLoading] = useState(false)
  const [reqTab,  setReqTab]  = useState('params')
  const [resTab,  setResTab]  = useState('body')
  const [configs, setConfigs] = useState([])
  const [showConfigs, setShowConfigs] = useState(false)
  const [saveName,setSaveName]= useState('')

  useEffect(() => { if (user) loadConfigs() }, [user])

  const loadConfigs = async () => {
    try {
      const res = await api.get('/configs')
      // FIX: type nằm trong config object (do backend lưu JSON.stringify({type,...config}))
      setConfigs(res.data.filter(c => (c.config?.type || 'http') === 'http'))
    } catch (_) {}
  }

  const buildUrl = () => {
    const ep = params.filter(p => p.enabled && p.key)
    if (!ep.length) return url
    const qs = ep.map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&')
    return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`
  }

  const buildHeaders = () => {
    const h = {}
    headers.filter(x => x.enabled && x.key).forEach(x => { h[x.key] = x.value })
    if (authType === 'api-key' && auth.key) h[auth.key] = auth.value
    return h
  }

  const buildAuth = () => {
    if (authType === 'basic')  return { type:'basic',  username: auth.username, password: auth.password }
    if (authType === 'bearer') return { type:'bearer', token:    auth.token }
    return null
  }

  const buildBody = () => {
    if (bodyType === 'none') return undefined
    if (bodyType === 'json') { try { return JSON.parse(body) } catch { return body } }
    return body
  }

  const sendRequest = async () => {
    if (!url.trim()) return toast.error('Vui lòng nhập URL')
    // user guard đã được handle bằng disabled button
    setLoading(true)
    setResult(null)
    try {
      const res = await api.post('/http-request', {
        method,
        url:     buildUrl(),
        headers: buildHeaders(),
        body:    buildBody(),
        auth:    buildAuth(),
        timeout: 30000,
      })
      setResult(res.data)
      setResTab('body')
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Lỗi không xác định'
      toast.error('Lỗi: ' + msg)
      setResult({ error: msg })
    } finally { setLoading(false) }
  }

  const saveConfig = async () => {
    if (!saveName.trim()) return toast.error('Nhập tên để lưu')
    try {
      await api.post('/configs', {
        name:   saveName.trim(),
        type:   'http',
        config: { method, url, headers, params, bodyType, body, authType, auth },
      })
      toast.success('Đã lưu!')
      setSaveName('')
      loadConfigs()
    } catch { toast.error('Lỗi khi lưu cấu hình') }
  }

  const loadConfig = (c) => {
    const cfg = c.config
    setMethod(  cfg.method   || 'GET')
    setUrl(     cfg.url      || '')
    setHeaders( cfg.headers  || [])
    setParams(  cfg.params   || [])
    setBodyType(cfg.bodyType || 'none')
    setBody(    cfg.body     || '')
    setAuthType(cfg.authType || 'none')
    setAuth(    cfg.auth     || { username:'', password:'', token:'', key:'', value:'' })
    setShowConfigs(false)
    toast.success(`Đã tải: ${c.name}`)
  }

  const deleteConfig = async (id) => {
    try { await api.delete(`/configs/${id}`); loadConfigs(); toast.success('Đã xóa') }
    catch { toast.error('Lỗi khi xóa') }
  }

  const statusColor = (s) => {
    if (!s) return 'var(--text3)'
    if (s < 300) return '#34d399'
    if (s < 400) return '#38bdf8'
    if (s < 500) return '#fbbf24'
    return '#f87171'
  }

  const fmt = (d) => typeof d === 'object' && d !== null ? JSON.stringify(d, null, 2) : String(d ?? '')

  const headerCount = headers.filter(h => h.enabled && h.key).length
  const paramCount  = params.filter(p => p.enabled && p.key).length

  return (
    <div style={{ maxWidth:900, margin:'0 auto' }} className="space-y-5">
      {/* Title */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
            background:'linear-gradient(135deg,#f59e0b,#ea580c)',
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Globe size={20} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize:18, fontWeight:700 }}>HTTP Request</h1>
            <p style={{ fontSize:13, color:'var(--text3)' }}>Kiểm thử REST API – tương tự Postman</p>
          </div>
        </div>
        {user && (
          <button className="btn btn-ghost" style={{ fontSize:13 }}
            onClick={() => setShowConfigs(v => !v)}>
            <FolderOpen size={14} /> Đã lưu ({configs.length})
          </button>
        )}
      </div>

      {/* Saved configs */}
      {showConfigs && (
        <div className="fade-in grad-border p-4">
          <h3 style={{ fontSize:14, fontWeight:600, color:'var(--text2)', marginBottom:12 }}>📁 Cấu hình đã lưu</h3>
          {configs.length === 0 ? (
            <p style={{ fontSize:13, color:'var(--text3)', textAlign:'center', padding:16 }}>Chưa có cấu hình nào</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:240, overflowY:'auto' }}>
              {configs.map(c => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 14px', borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)' }}>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:600 }}>{c.name}</div>
                    <div style={{ fontSize:12, fontFamily:'JetBrains Mono,monospace', color:'var(--text3)',
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <span style={{ color: METHOD_COLORS[c.config?.method] }}>{c.config?.method}</span>
                      {' '}{c.config?.url}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8, marginLeft:12, flexShrink:0 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => loadConfig(c)}>Tải</button>
                    <button onClick={() => deleteConfig(c.id)}
                      style={{ color:'var(--danger)', background:'none', border:'none', cursor:'pointer', padding:4 }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Request builder */}
      <div className="grad-border p-5 space-y-4">
        {/* URL bar – dùng inline flex để đảm bảo nút Gửi luôn hiện */}
        <div style={{ display:'flex', gap:8, alignItems:'stretch' }}>
          <select
            style={{ width:100, flexShrink:0, background:'var(--bg3)', border:'1.5px solid var(--border)',
              color: METHOD_COLORS[method] || 'var(--text)', borderRadius:7, padding:'0 10px',
              fontFamily:'JetBrains Mono,monospace', fontSize:13, fontWeight:700, cursor:'pointer' }}
            value={method} onChange={e => setMethod(e.target.value)}>
            {METHODS.map(m => <option key={m} value={m} style={{ color: METHOD_COLORS[m] }}>{m}</option>)}
          </select>

          <input className="inp font-mono" style={{ flex:1, minWidth:0, fontSize:13 }}
            placeholder="https://api.example.com/endpoint"
            value={url} onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loading && user && sendRequest()} />

          <button
            style={{ flexShrink:0 }}
            className={`btn ${user ? 'btn-primary' : 'btn-secondary'}`}
            onClick={sendRequest}
            disabled={loading || !user}
            title={!user ? 'Đăng nhập để gửi request' : ''}
          >
            {loading
              ? <><Loader size={14} className="animate-spin" /> Đang gửi...</>
              : !user
                ? <><Lock size={14} /> Đăng nhập</>
                : <><Play size={14} /> Gửi</>
            }
          </button>
        </div>

        {/* Tabs */}
        <div className="tab-bar">
          {[
            { id:'params',  label:'Query Params', count: paramCount  },
            { id:'headers', label:'Headers',      count: headerCount },
            { id:'body',    label:'Body'   },
            { id:'auth',    label:'Auth'   },
          ].map(t => (
            <div key={t.id} className={`tab-item ${reqTab===t.id?'active':''}`}
              onClick={() => setReqTab(t.id)}>
              {t.label}
              {t.count > 0 && (
                <span style={{ marginLeft:4, background:'var(--accent)', color:'#000',
                  fontSize:10, fontWeight:700, padding:'1px 5px', borderRadius:10 }}>
                  {t.count}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ minHeight:120 }}>
          {reqTab==='params'  && <KVEditor label="Query Parameters" items={params}  onChange={setParams} />}
          {reqTab==='headers' && <KVEditor label="Headers"          items={headers} onChange={setHeaders} />}

          {reqTab==='body' && (
            <div className="space-y-3">
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {BODY_TYPES.map(t => (
                  <button key={t} onClick={() => setBodyType(t)}
                    className={`chip cursor-pointer ${bodyType===t?'chip-blue':''}`}
                    style={bodyType!==t ? { background:'var(--bg)', color:'var(--text3)', border:'1px solid var(--border)' } : {}}>
                    {t}
                  </button>
                ))}
              </div>
              {bodyType !== 'none' && (
                <textarea className="inp font-mono" rows={8} style={{ fontSize:13 }}
                  value={body} onChange={e => setBody(e.target.value)}
                  placeholder={
                    bodyType==='json' ? '{\n  "key": "value"\n}'
                    : bodyType==='xml' ? '<?xml version="1.0"?>\n<root></root>'
                    : 'key=value&key2=value2'
                  } />
              )}
            </div>
          )}

          {reqTab==='auth' && (
            <div className="space-y-3">
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Kiểu xác thực</label>
                <select className="inp" style={{ width:200 }} value={authType}
                  onChange={e => setAuthType(e.target.value)}>
                  <option value="none">Không có</option>
                  <option value="basic">Basic Auth</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="api-key">API Key (Header)</option>
                </select>
              </div>
              {authType==='basic' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={{ display:'block', fontSize:12, color:'var(--text3)', marginBottom:4 }}>Username</label>
                    <input className="inp" value={auth.username} onChange={e => setAuth(a=>({...a,username:e.target.value}))} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, color:'var(--text3)', marginBottom:4 }}>Password</label>
                    <input className="inp" type="password" value={auth.password} onChange={e => setAuth(a=>({...a,password:e.target.value}))} />
                  </div>
                </div>
              )}
              {authType==='bearer' && (
                <div>
                  <label style={{ display:'block', fontSize:12, color:'var(--text3)', marginBottom:4 }}>Bearer Token</label>
                  <input className="inp font-mono" style={{ fontSize:13 }} placeholder="eyJhbGci..."
                    value={auth.token} onChange={e => setAuth(a=>({...a,token:e.target.value}))} />
                </div>
              )}
              {authType==='api-key' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={{ display:'block', fontSize:12, color:'var(--text3)', marginBottom:4 }}>Header Name</label>
                    <input className="inp" placeholder="X-API-Key" value={auth.key} onChange={e => setAuth(a=>({...a,key:e.target.value}))} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:12, color:'var(--text3)', marginBottom:4 }}>Value</label>
                    <input className="inp font-mono" style={{ fontSize:13 }} value={auth.value} onChange={e => setAuth(a=>({...a,value:e.target.value}))} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save / Login notice */}
        {user ? (
          <div style={{ display:'flex', gap:8, paddingTop:12, borderTop:'1px solid var(--border)' }}>
            <input className="inp" style={{ flex:1, fontSize:13 }}
              placeholder="Nhập tên để lưu cấu hình này..."
              value={saveName} onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => e.key==='Enter' && saveConfig()} />
            <button className="btn btn-ghost" style={{ fontSize:13, flexShrink:0 }} onClick={saveConfig}>
              <Save size={13} /> Lưu
            </button>
          </div>
        ) : (
          <div style={{ fontSize:13, color:'var(--text3)', padding:'10px 14px',
            borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)',
            display:'flex', alignItems:'center', gap:8 }}>
            <Lock size={14} />
            Đăng nhập để gửi HTTP request và lưu cấu hình
          </div>
        )}
      </div>

      {/* Response */}
      {result && (
        <div className="fade-in grad-border p-5">
          {result.error ? (
            <>
              <div style={{ fontSize:15, fontWeight:700, color:'var(--danger)', marginBottom:12 }}>❌ Request thất bại</div>
              <div className="log-output log-error">{result.error}</div>
            </>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
                <span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:28, fontWeight:800,
                  color: statusColor(result.status) }}>
                  {result.status}
                </span>
                <span style={{ fontSize:14, color:'var(--text2)' }}>{result.statusText}</span>
                {result.latency && (
                  <span className="chip chip-blue" style={{ fontFamily:'JetBrains Mono,monospace' }}>{result.latency}ms</span>
                )}
                {result.size != null && (
                  <span className="chip chip-orange">
                    {result.size < 1024 ? `${result.size} B` : `${(result.size/1024).toFixed(1)} KB`}
                  </span>
                )}
              </div>
              <div className="tab-bar" style={{ marginBottom:12 }}>
                {[{id:'body',label:'Body'},{id:'headers',label:'Headers'}].map(t => (
                  <div key={t.id} className={`tab-item ${resTab===t.id?'active':''}`}
                    onClick={() => setResTab(t.id)}>{t.label}</div>
                ))}
              </div>
              <pre className="log-output" style={{ whiteSpace:'pre-wrap', wordBreak:'break-all', fontSize:13 }}>
                {resTab==='body' ? fmt(result.data) : fmt(result.headers)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  )
}
